/**
 * Station geometry.
 *
 * A station is a flat deck — a slab, not a sphere or a spire — and its size is
 * decided by what it has to carry. Pads are packed first and the outline is
 * fitted around them afterwards, so a station is physically as large as its
 * capability rather than being sized arbitrarily and hoping the pads fit.
 *
 * Derived from the seed like everything else and stored nowhere.
 *
 * ── Two consumers, one description ──────────────────────────────────────────
 *
 * The starmap draws only `outline` and ignores the rest: from across a system
 * you should see that a station is there and how big it is, not count its
 * berths. Flight uses the whole thing. Nothing here is map-specific, so there
 * is one source of truth about where a pad actually is.
 *
 * Both callers build the whole description — with at most eighteen pads the
 * packing is a short loop, and a separate cheap path for the map would be two
 * things to keep in agreement for no measurable gain.
 */
import { Rng } from './rng.js';
import { PAD_SIZES, } from './types.js';
/**
 * Deck units per world unit.
 *
 * Packing is done in *deck units*, where a pad's class size (25 through 100) is
 * its side length — those numbers come from the design doc and reasoning about
 * layout in them is far easier than in a scaled-down space. This converts the
 * finished layout into the units everything else uses.
 *
 * It exists because stations were built at the same scale as worlds: a large
 * station came out wider than a small planet, which made docking read as
 * landing on a continent. A station should be a speck against a planet, so the
 * whole deck is brought down by an order of magnitude at the end — the layout
 * is untouched, only the size it is finally expressed at.
 */
export const STATION_SCALE = 0.05;
/** Clear space between neighbouring pads, for approach lanes. */
const PAD_GAP = 11;
/**
 * Deck that extends past the outermost pads, before the outline is cut.
 *
 * Kept tight. Generous margins plus a hollow centre left decks reading as
 * mostly empty plate with a few berths lost on it.
 */
const DECK_MARGIN = 10;
/** Deck thickness relative to its short side. */
const THICKNESS_RATIO = { min: 0.05, max: 0.11 };
/** Pads sorted biggest first — every layout wants the awkward ones placed early. */
function bySizeDescending(pads) {
    return [...pads].sort((a, b) => PAD_SIZES[b.size] - PAD_SIZES[a.size]);
}
function placement(pad, x, y) {
    return { padId: pad.id, size: pad.size, position: { x, y }, extent: PAD_SIZES[pad.size] };
}
/**
 * Shelf packing, largest pad first.
 *
 * Rows are filled left to right and a new row starts when the next pad will not
 * fit. Deterministic, never overlaps, and leaves a full gap between every pair
 * — which is the approach lane. Fancier packing would win a little area and
 * cost the guarantee.
 */
function packPads(pads, targetWidth) {
    const ordered = [...pads].sort((a, b) => PAD_SIZES[b.size] - PAD_SIZES[a.size]);
    const placements = [];
    let cursorX = 0;
    let rowTop = 0;
    let rowHeight = 0;
    let widest = 0;
    for (const pad of ordered) {
        const extent = PAD_SIZES[pad.size];
        // Wrap to a new row, unless this row is empty — a pad wider than the target
        // still has to go somewhere.
        if (cursorX > 0 && cursorX + extent > targetWidth) {
            rowTop += rowHeight + PAD_GAP;
            cursorX = 0;
            rowHeight = 0;
        }
        placements.push({
            padId: pad.id,
            size: pad.size,
            position: { x: cursorX + extent / 2, y: rowTop + extent / 2 },
            extent,
        });
        cursorX += extent + PAD_GAP;
        widest = Math.max(widest, cursorX - PAD_GAP);
        rowHeight = Math.max(rowHeight, extent);
    }
    return { placements, width: widest, height: rowTop + rowHeight };
}
// ─── Outline ─────────────────────────────────────────────────────────────────
/**
 * Deck footprint: the packed area, expanded by a margin, with its corners cut.
 *
 * Each corner is chamfered independently, which is what makes one station read
 * as a hexagon, another as a wedge, another as a plain slab. The cut is capped
 * at the margin, so it can never reach the packed pads however the seed rolls —
 * containment is guaranteed by construction rather than checked afterwards.
 */
/**
 * Rings of pads around a centre, and a polygon disc that contains them.
 *
 * Each ring is filled until its angular budget runs out, then the radius steps
 * outward by the widest pad it held. The outline radius is measured from the
 * furthest pad *corner*, so containment holds without a separate check.
 */
function layoutDisc(rng, pads) {
    const ordered = bySizeDescending(pads);
    const placements = [];
    /**
     * Centre-to-centre distance two axis-aligned squares need.
     *
     * Measured on the diagonal: two squares offset at 45 degrees touch corner to
     * corner while their axis separations still look fine, so the side length
     * alone under-counts. This is what the first attempt got wrong.
     */
    const chordFor = (extent) => extent * Math.SQRT2 + PAD_GAP;
    // The largest pad takes the middle. Ringing everything around an empty hub
    // wasted the one part of the deck that is always reachable, and made small
    // stations read as doughnuts.
    const hub = ordered[0];
    const hubExtent = PAD_SIZES[hub.size];
    placements.push(placement(hub, 0, 0));
    let index = 1;
    // First ring has to clear the hub pad, measured on the diagonal like the rest.
    let radius = index < ordered.length
        ? ((hubExtent + PAD_SIZES[ordered[1].size]) * Math.SQRT2) / 2 + PAD_GAP
        : hubExtent;
    /** Arc a pad of this size consumes on a ring of this radius. */
    const arcFor = (extent, at) => {
        const ratio = chordFor(extent) / (2 * at);
        return ratio >= 1 ? Infinity : 2 * Math.asin(ratio);
    };
    while (index < ordered.length) {
        // Fill the ring against each pad's own arc, not the widest one's.
        //
        // Sizing every berth on a ring as if it were the largest made the ring's
        // capacity a bad underestimate, and the pads that did not fit were exiled
        // to a ring of their own — one leftover small pad could double the station's
        // radius. Most nine-pad discs now close in a single ring.
        const ring = [];
        let budget = 0;
        while (index + ring.length < ordered.length) {
            const next = ordered[index + ring.length];
            const arc = arcFor(PAD_SIZES[next.size], radius);
            if (budget + arc > Math.PI * 2)
                break;
            budget += arc;
            ring.push(next);
        }
        if (ring.length === 0) {
            // Not even the smallest remaining pad fits at this radius. Step out by
            // enough that it will, rather than by a fixed guess.
            radius += PAD_SIZES[ordered[ordered.length - 1].size];
            continue;
        }
        // Slack is shared evenly, so the pads sit spread around the ring rather
        // than crowded on one side with the whole gap behind them.
        const slack = (Math.PI * 2 - budget) / ring.length;
        let angle = rng.range(0, Math.PI * 2);
        let widestInRing = 0;
        for (const pad of ring) {
            const extent = PAD_SIZES[pad.size];
            const step = (arcFor(extent, radius) + slack) / 2;
            angle += step;
            placements.push(placement(pad, Math.cos(angle) * radius, Math.sin(angle) * radius));
            angle += step;
            widestInRing = Math.max(widestInRing, extent);
            index++;
        }
        if (index >= ordered.length)
            break;
        // Clear this ring's widest and the next ring's widest, half of each rather
        // than a whole one — but on the diagonal. Pads on neighbouring rings meet at
        // whatever angle the two rings' spacings happen to produce, so the corner is
        // what has to clear, not the side.
        radius +=
            ((widestInRing + PAD_SIZES[ordered[index].size]) * Math.SQRT2) / 2 + PAD_GAP;
    }
    const reach = placements.reduce((max, p) => Math.max(max, Math.hypot(p.position.x, p.position.y) + (p.extent * Math.SQRT2) / 2), 0);
    const segments = rng.int(9, 16);
    // The deck is a polygon, not a circle: its edges bow inward of the radius its
    // vertices sit on. Sizing by the vertex radius let pads near an edge midpoint
    // hang over the side, so the *inradius* is what has to clear the pads and the
    // vertex radius is derived back from it.
    const outer = (reach + DECK_MARGIN) / Math.cos(Math.PI / segments);
    const outline = Array.from({ length: segments }, (_, i) => {
        const theta = (i / segments) * Math.PI * 2;
        return { x: Math.cos(theta) * outer, y: Math.sin(theta) * outer };
    });
    return { placements, outline };
}
/**
 * Four spars from a hub, pads strung along them, and a plus-shaped outline.
 *
 * Arms share a uniform half-width set by the widest pad, so a pad placed on the
 * centre line of any arm is inside the outline by construction.
 */
function layoutCross(rng, pads) {
    const ordered = bySizeDescending(pads);
    const halfWidth = PAD_SIZES[ordered[0].size] / 2 + DECK_MARGIN;
    // The hub is the widest part of the deck and was standing empty; the largest
    // pad goes there and the arms carry the rest.
    const hub = ordered[0];
    const rest = ordered.slice(1);
    // Dealt round-robin so the arms end up comparable rather than one long spar.
    const arms = [[], [], [], []];
    rest.forEach((pad, i) => arms[i % 4].push(pad));
    const directions = [
        { x: 1, y: 0 },
        { x: 0, y: 1 },
        { x: -1, y: 0 },
        { x: 0, y: -1 },
    ];
    const placements = [placement(hub, 0, 0)];
    let longest = halfWidth;
    arms.forEach((arm, i) => {
        const direction = directions[i];
        let along = PAD_SIZES[hub.size] / 2 + PAD_GAP;
        for (const pad of arm) {
            const extent = PAD_SIZES[pad.size];
            const centre = along + extent / 2;
            placements.push(placement(pad, direction.x * centre, direction.y * centre));
            along = centre + extent / 2 + PAD_GAP;
        }
        longest = Math.max(longest, along + DECK_MARGIN - PAD_GAP);
    });
    const w = halfWidth;
    const l = longest;
    const outline = [
        { x: w, y: -w }, { x: l, y: -w }, { x: l, y: w }, { x: w, y: w },
        { x: w, y: l }, { x: -w, y: l }, { x: -w, y: w }, { x: -l, y: w },
        { x: -l, y: -w }, { x: -w, y: -w }, { x: -w, y: -l }, { x: w, y: -l },
    ];
    return { placements, outline };
}
function buildOutline(rng, halfWidth, halfHeight) {
    const limit = Math.min(DECK_MARGIN, halfWidth * 0.45, halfHeight * 0.45);
    const cut = () => (rng.bool(0.75) ? rng.range(limit * 0.25, limit) : 0);
    const [a, b, c, d] = [cut(), cut(), cut(), cut()];
    const outline = [];
    // Counter-clockwise from the bottom-left, cutting each corner in turn.
    outline.push({ x: -halfWidth + a, y: -halfHeight });
    outline.push({ x: halfWidth - b, y: -halfHeight });
    if (b > 0)
        outline.push({ x: halfWidth, y: -halfHeight + b });
    outline.push({ x: halfWidth, y: halfHeight - c });
    if (c > 0)
        outline.push({ x: halfWidth - c, y: halfHeight });
    outline.push({ x: -halfWidth + d, y: halfHeight });
    if (d > 0)
        outline.push({ x: -halfWidth, y: halfHeight - d });
    outline.push({ x: -halfWidth, y: -halfHeight + a });
    return outline;
}
// ─── Props ───────────────────────────────────────────────────────────────────
const PROP_ROLES = ['tower', 'module', 'mast', 'dish'];
/** Deck kept clear around every pad, so a berth always has an approach. */
export const PAD_CLEARANCE = 3;
/** Breathing room between neighbouring props — a seam, not a walkway. */
const PROP_GAP = 0.15;
/** Below this a prop is grit on the deck rather than structure. */
const MIN_HALF = 0.8;
/** Shrink-to-fit can produce slivers; past this ratio they read as scratches. */
const MAX_ASPECT = 6;
/** Does this box, grown by clearance, touch any pad? */
function clearOfPads(position, halfSize, pads, clearance) {
    for (const pad of pads) {
        const reach = pad.extent / 2 + halfSize + clearance;
        if (Math.abs(position.x - pad.position.x) < reach &&
            Math.abs(position.y - pad.position.y) < reach) {
            return false;
        }
    }
    return true;
}
/**
 * Surface clutter in the leftover deck space.
 *
 * Candidates are rejected rather than repositioned when they clash with a pad —
 * a station whose spare room is genuinely full simply gets fewer props, which
 * is correct. Nothing may encroach on a berth or its approach.
 */
function insideOutline(point, outline) {
    let inside = false;
    for (let i = 0, j = outline.length - 1; i < outline.length; j = i++) {
        const a = outline[i];
        const b = outline[j];
        if (a.y > point.y === b.y > point.y)
            continue;
        const crossing = ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
        if (point.x < crossing)
            inside = !inside;
    }
    return inside;
}
function scatterProps(rng, halfWidth, halfHeight, pads, outline) {
    const props = [];
    /**
     * Grid fill at halving scales, like a quadtree.
     *
     * One grid size can only fill the space its own cells fit into, which left a
     * deck at roughly a third covered — big blocks between the pads and bare
     * plate everywhere they did not reach. Each finer pass gets into what the
     * coarser one had to skip, and the structure that produces (large blocks
     * broken up by smaller ones) also reads better than a uniform tiling.
     */
    const coarsest = Math.max(10, Math.min(halfWidth, halfHeight) / 4);
    /**
     * Axis-aligned half-extents of a prop as it actually sits.
     *
     * Props are only ever turned by a quarter, which swaps width and depth and
     * leaves the box axis-aligned. Free rotation forced every clearance test to
     * reserve the diagonal — a square's corners reach 1.41 times its half-side —
     * and that inflation, applied on both the prop-to-prop spacing and the
     * containment check, was costing more deck than the varied angles were worth.
     */
    const halfExtent = (size, rotation) => rotation === 0 ? { x: size.x / 2, y: size.y / 2 } : { x: size.y / 2, y: size.x / 2 };
    const padBlockers = pads.map((p) => ({
        position: p.position,
        half: { x: p.extent / 2 + PAD_CLEARANCE, y: p.extent / 2 + PAD_CLEARANCE },
    }));
    /**
     * Placed props, bucketed by position.
     *
     * Shrink-to-fit asks "what is in the way?" repeatedly, and a large station
     * ends up with hundreds of props — scanning all of them per round made
     * generating one station take the best part of a second. A prop is never
     * wider than the coarsest cell, so a bucket that size means a query only has
     * to look at the nine cells around the candidate.
     */
    const bucketSize = Math.max(coarsest, 4);
    const buckets = new Map();
    // Decks are centred on the origin, so coordinates are signed; the offset
    // keeps bucket indices non-negative and the key collision-free.
    const keyOf = (i, j) => (i + 4096) * 8192 + (j + 4096);
    const cellOf = (v) => Math.floor(v / bucketSize);
    const addBlocker = (blocker) => {
        for (let i = cellOf(blocker.position.x - blocker.half.x); i <= cellOf(blocker.position.x + blocker.half.x); i++) {
            for (let j = cellOf(blocker.position.y - blocker.half.y); j <= cellOf(blocker.position.y + blocker.half.y); j++) {
                const key = keyOf(i, j);
                const list = buckets.get(key);
                if (list)
                    list.push(blocker);
                else
                    buckets.set(key, [blocker]);
            }
        }
    };
    /** The first blocker overlapping this box, or undefined. Pads first, then props. */
    const overlapping = (position, half) => {
        const clashes = (b) => Math.abs(b.position.x - position.x) < b.half.x + half.x &&
            Math.abs(b.position.y - position.y) < b.half.y + half.y;
        const pad = padBlockers.find(clashes);
        if (pad)
            return pad;
        for (let i = cellOf(position.x - half.x); i <= cellOf(position.x + half.x); i++) {
            for (let j = cellOf(position.y - half.y); j <= cellOf(position.y + half.y); j++) {
                const found = buckets.get(keyOf(i, j))?.find(clashes);
                if (found)
                    return found;
            }
        }
        return undefined;
    };
    /**
     * The largest box centred here that clears everything, up to what was asked.
     *
     * Rejecting a candidate outright was what left the deck a quarter bare. The
     * space that survives pad packing is mostly *bands* — the eleven-unit lane
     * between two pad rows, the margin strip along an edge — and a grid cell
     * either straddles a band or misses it. Halving the grid again and again
     * chases that but never catches it, because the band's width has nothing to
     * do with the grid's.
     *
     * Shrinking to fit does catch it. Two boxes clear each other as soon as they
     * are separated on *either* axis, so for each blocker there are two ways out
     * and the cheaper one is taken — which is what turns a cell overlapping a pad
     * lane into a slab filling the lane beside it.
     */
    const fitted = (position, want) => {
        let half = { ...want };
        // One blocker can be re-hit after another shrink, so iterate rather than
        // sweeping once. Bounded because every round strictly shrinks the box.
        for (let round = 0; round < 24; round++) {
            const hit = overlapping(position, half);
            if (!hit) {
                return half.x >= MIN_HALF &&
                    half.y >= MIN_HALF &&
                    Math.max(half.x, half.y) / Math.min(half.x, half.y) <= MAX_ASPECT
                    ? half
                    : null;
            }
            const needX = Math.abs(hit.position.x - position.x) - hit.half.x;
            const needY = Math.abs(hit.position.y - position.y) - hit.half.y;
            if (needX < MIN_HALF && needY < MIN_HALF)
                return null;
            // Give up the axis that costs the least area.
            if (half.x - needX <= half.y - needY)
                half = { ...half, x: needX };
            else
                half = { ...half, y: needY };
        }
        return null;
    };
    for (let pass = 0; pass < 7; pass++) {
        const cell = coarsest / 2 ** pass;
        if (cell < 1.2)
            break;
        const columns = Math.ceil((halfWidth * 2) / cell);
        const rows = Math.ceil((halfHeight * 2) / cell);
        for (let row = 0; row < rows; row++) {
            for (let column = 0; column < columns; column++) {
                // A few deliberate gaps at the coarsest scale, so a deck reads as built
                // rather than tiled. Finer passes fill in around them.
                if (pass === 0 && rng.bool(0.1))
                    continue;
                const footprint = cell * rng.range(0.94, 1.0);
                const want = { x: footprint / 2, y: (footprint * rng.range(0.9, 1.0)) / 2 };
                const rotation = rng.bool(0.75) ? 0 : Math.PI / 2;
                const jitter = (cell - footprint) / 2;
                const position = {
                    x: -halfWidth + (column + 0.5) * cell + rng.range(-jitter, jitter),
                    y: -halfHeight + (row + 0.5) * cell + rng.range(-jitter, jitter),
                };
                let half = fitted(position, rotation === 0 ? want : { x: want.y, y: want.x });
                if (!half)
                    continue;
                // The deck is the outline, not the rectangle it was cut from. Sampling
                // the rectangle put props out past chamfered corners, hanging in space.
                //
                // Shrink rather than reject, for the same reason candidates shrink around
                // pads: along a chamfer or a disc's rim every cell overhangs a little,
                // and discarding them all left a bare band right around the edge — the
                // most visible part of the silhouette.
                const contained = (h) => insideOutline({ x: position.x - h.x, y: position.y - h.y }, outline) &&
                    insideOutline({ x: position.x + h.x, y: position.y - h.y }, outline) &&
                    insideOutline({ x: position.x + h.x, y: position.y + h.y }, outline) &&
                    insideOutline({ x: position.x - h.x, y: position.y + h.y }, outline);
                while (!contained(half)) {
                    half = { x: half.x * 0.7, y: half.y * 0.7 };
                    if (half.x < MIN_HALF || half.y < MIN_HALF)
                        break;
                }
                if (!contained(half))
                    continue;
                // `half` is the box on the deck; `size` is the box before its quarter
                // turn, since that is what the mesh is built from.
                const size = rotation === 0
                    ? { x: half.x * 2, y: half.y * 2 }
                    : { x: half.y * 2, y: half.x * 2 };
                const role = rng.weighted([
                    ['module', 54],
                    ['tower', 20],
                    ['dish', 14],
                    ['mast', 12],
                ]);
                addBlocker({
                    position,
                    half: { x: half.x + PROP_GAP, y: half.y + PROP_GAP },
                });
                props.push({
                    id: `prop-${props.length}`,
                    role,
                    position,
                    size: {
                        ...size,
                        // Masts are thin and tall; everything else stays low so it never
                        // obscures a landing approach.
                        z: role === 'mast'
                            ? rng.range(coarsest * 0.9, coarsest * 1.9)
                            : rng.range(cell * 0.35, cell * 1.1),
                    },
                    rotation,
                });
            }
        }
    }
    return props;
}
// ─── Entry point ─────────────────────────────────────────────────────────────
/**
 * The full physical description of a station.
 *
 * Pure in the station's id and pads, so two callers — or a client and a
 * server — always agree on where every berth is.
 */
/** Row-packed pads under a chamfered rectangle — the original slab form. */
function layoutSlab(rng, pads) {
    // Aim for a roughly square deck, varied per station. Derived from the pads'
    // own area so the deck grows with what it carries.
    const padArea = pads.reduce((total, pad) => total + PAD_SIZES[pad.size] ** 2, 0);
    const packed = packPads(pads, Math.sqrt(padArea) * rng.range(0.95, 1.45));
    // Recentre so the deck origin is its middle, which is what everything
    // downstream — orbits, docking, rendering — expects.
    const offsetX = packed.width / 2;
    const offsetY = packed.height / 2;
    return {
        placements: packed.placements.map((p) => ({
            ...p,
            position: { x: p.position.x - offsetX, y: p.position.y - offsetY },
        })),
        outline: buildOutline(rng, offsetX + DECK_MARGIN, offsetY + DECK_MARGIN),
    };
}
/**
 * Which form a station takes.
 *
 * Crosses need arms worth walking down, so they are reserved for stations with
 * enough pads to spread across four spars. A two-pad outpost as a cross would
 * be a plus sign with one berth per arm and nothing in between.
 */
function rollShape(rng, padCount) {
    if (padCount < 4)
        return rng.bool(0.65) ? 'slab' : 'disc';
    return rng.weighted([
        ['slab', 40],
        ['disc', 35],
        ['cross', 25],
    ]);
}
export function getStationGeometry(seed, station) {
    const rng = Rng.forEntity(seed, 'station-geometry', station.id);
    const shape = rollShape(rng, station.pads.length);
    const { placements, outline } = shape === 'disc'
        ? layoutDisc(rng, station.pads)
        : shape === 'cross'
            ? layoutCross(rng, station.pads)
            : layoutSlab(rng, station.pads);
    const radius = outline.reduce((max, p) => Math.max(max, Math.hypot(p.x, p.y)), 0);
    const bounds = outline.reduce((box, p) => ({ x: Math.max(box.x, Math.abs(p.x)), y: Math.max(box.y, Math.abs(p.y)) }), { x: 0, y: 0 });
    const thickness = Math.min(bounds.x, bounds.y) * 2 * rng.range(THICKNESS_RATIO.min, THICKNESS_RATIO.max);
    const props = scatterProps(rng, bounds.x, bounds.y, placements, outline);
    // Converted to world units only here, once everything is placed. Doing it any
    // earlier would mean every clearance and margin above had to be expressed in
    // scaled units, and the layout reasoning would be that much harder to follow.
    const s = STATION_SCALE;
    return {
        stationId: station.id,
        shape,
        outline: outline.map((p) => ({ x: p.x * s, y: p.y * s })),
        thickness: thickness * s,
        radius: radius * s,
        pads: placements.map((p) => ({
            ...p,
            position: { x: p.position.x * s, y: p.position.y * s },
            extent: p.extent * s,
        })),
        props: props.map((p) => ({
            ...p,
            position: { x: p.position.x * s, y: p.position.y * s },
            size: { x: p.size.x * s, y: p.size.y * s, z: p.size.z * s },
        })),
    };
}
//# sourceMappingURL=station.js.map